<template>
  <div style="margin-top: -5px">
    <b-card no-body class="mb-0 table-no-top table-text-nowrap">
      <b-table
        ref="refProfileListTable"
        class="position-relative mb-25 overflow-auto last-child-nb table-bottom-round table-small table-alternate-bg"
        :items="fetchStudentSections"
        responsive
        :fields="sectionFields"
        primary-key="id"
        :sort-by.sync="sortBy"
        no-provider-sorting
        :sort-compare="sectionSortCompare"
        show-empty
        empty-text=""
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Course -->
        <template #cell(name)="data">
          <div class="d-flex align-items-center" style="gap: 0.625rem" v-if="data.item.course">
            <div class="d-flex justify-content-center align-items-center card card-bg mb-0" style="width: 2.25rem; height: 2.25rem">
              <h4 class="mb-0">{{ data.item.index }}</h4>
            </div>
            <b-avatar
              size="36"
              :src="data.item.course.avatar"
              :text="avatarText(data.item.course.name)"
              :variant="`light-info`"
              :to="{ name: 'apps-roster-course-view', params: { id: data.item.course.id } }"
              style="margin-top: 2px"
              rounded
            />
            <div class="d-flex flex-column" style="gap: 0">
              <b-link
                :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.id } }"
                class="font-weight-bold text-nowrap text-white mb-0"
              >
                {{ resolveCourseSectionName(data.item) }}
              </b-link>
              <b-link
                :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.id } }"
                class="d-flex align-items-center text-muted text-nowrap"
              >
                <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 1px" />
                <small> {{ data.item.course.name }}</small>
              </b-link>
            </div>
          </div>
        </template>

        <!-- Column: Attributes - Program -->
        <template #cell(fundingSource)="data">
          <b-badge :variant="`light-${resolveProfileProgramVariant(data.item.profile.attributes.fundingSource)}`" class="text-capitalize">
            {{ title(data.item.profile.attributes.fundingSource) }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="status"
            name="section status"
            :options="editableSectionStatusOptions"
            :resolve-variant="resolveProfileSectionStatusVariant"
            :light="false"
            @updated="refreshSections"
          />
        </template>

        <!-- Column: Course Exam Voucher -->
        <template #cell(examVoucher)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="examVoucher"
            name="exam voucher"
            :options="examVoucherOptions"
            :resolve-variant="resolveProfileYNVariant"
          />
        </template>

        <!-- Column: Course Exam Taken? -->
        <template #cell(examTaken)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="examTaken"
            name="exam taken"
            :options="yesNoOptions"
            :resolve-variant="resolveProfileYNVariant"
          />
        </template>

        <!-- Column: Course Exam Passed? -->
        <template #cell(examPassed)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="examPassed"
            name="exam passed"
            :options="yesNoOptions"
            :resolve-variant="resolveProfileYNVariant"
          />
        </template>

        <!-- Column: Course Exam Passed? -->
        <!-- Column: Retake (this section) -->
        <template #cell(retake)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="retake"
            name="retake"
            :options="yesNoActualOptions"
            :resolve-variant="resolveProfileYNVariant"
          />
        </template>

        <!-- Column: Makeup -->
        <template #cell(makeup)="data">
          <action-badge-dropdown
            :value="sectionMakeupEnabled(data.item) ? 'yes' : 'no'"
            :options="yesNoActualOptions"
            :resolve-variant="resolveProfileYNVariant"
            :disabled="!$can('roster.profile.update')"
            @select="(value) => setSectionMakeup(data.item, value === 'yes')"
          />
        </template>

        <!-- Column: Transfer of Credit (this section) -->
        <template #cell(toc)="data">
          <action-badge-dropdown
            :value="sectionCreditEnabled(data.item) ? 'yes' : 'no'"
            :options="yesNoActualOptions"
            :resolve-variant="resolveProfileYNVariant"
            :disabled="!$can('roster.cohort.student.credit')"
            @select="(value) => setSectionCredit(data.item, value === 'yes')"
          />
        </template>

        <template #cell(labs)="data">
          <profile-section-field-select
            :profile="data.item.profile"
            :section-id="data.item.id"
            field="labCompleted"
            name="labs"
            :options="yesNoOptions"
            :resolve-variant="resolveProfileYNVariant"
          />
        </template>

        <!-- Column: Attributes - Payment Status -->
        <!-- <template #cell(paymentStatus)="data">
  <b-badge :variant="`${resolveProfilePaymentStatusVariant(data.item.profile.attributes.paymentStatus)}`" class="text-capitalize">
    {{ title(data.item.profile.attributes.paymentStatus) }}
  </b-badge>
</template> -->

        <!-- Column: Attributes - Uploaded Resume -->
        <template #cell(uploadedResume)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-resume-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'resume')"
            />

            <b-tooltip title="Upload Resume" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-resume-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedResume)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedResume) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded COE -->
        <template #cell(uploadedCoe)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-coe-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'coe')"
            />

            <b-tooltip title="Upload COE" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-coe-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedCoe)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedCoe) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded Transcript -->
        <template #cell(uploadedTranscript)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-transcript-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'transcript', 'prior')"
            />

            <b-tooltip title="Upload Transcript" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-transcript-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedTranscript)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedTranscript) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - VA Submission -->
        <template #cell(submittedToVa)="data">
          <profile-submitted-to-va-select :profile="data.item.profile" />
        </template>

        <!-- Column: Total Students -->
        <template #cell(sessionDate)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">
              {{ moment(data.item.sessionDate).format('L') }}
            </span>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import ProfileSubmittedToVaSelect from '@/views/apps/roster/profile/components/ProfileSubmittedToVaSelect.vue';
import ProfileSectionFieldSelect from '@/views/apps/roster/profile/components/ProfileSectionFieldSelect.vue';
import ActionBadgeDropdown from '@/views/apps/roster/profile/components/ActionBadgeDropdown.vue';
import sectionRowActions from '@/views/apps/roster/profile/sectionRowActions';
import { sectionStatusOptions, examVoucherOptions, yesNoOptions, yesNoActualOptions } from '@/views/apps/roster/profile/profileOptions';
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from 'bootstrap-vue';
import { avatarText, title } from '@core/utils/filter';
import usePersistedPerPage from '@/libs/utils/perPage';
import store from '@/store';
import { onUnmounted, ref } from '@vue/composition-api';
import studentStoreModule from '@/store/student/studentStoreModule';
import moment from 'moment';
import useCourseSections from '@/views/apps/roster/courses/sections/useCourseSections';
import { momentTzFormatted } from '@/libs/utils/time';
import { hexToRGB } from '@/libs/utils/color';
import useProfiles from '@/views/apps/roster/profile/useProfiles';
import VueContext from 'vue-context';
import vSelect from 'vue-select';

export default {
  mixins: [sectionRowActions],
  emits: ['add-credit', 'changed'],
  components: {
    ProfileSubmittedToVaSelect,
    ProfileSectionFieldSelect,
    ActionBadgeDropdown,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    VueContext,
    vSelect,
  },
  props: {
    studentId: {
      type: String,
      required: true,
    },
    profileId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      examVoucherOptions,
      yesNoOptions,
      yesNoActualOptions,
      // Archiving is not offered inline, matching the other status dropdowns.
      editableSectionStatusOptions: sectionStatusOptions.filter((option) => option.value !== 'archived'),
    };
  },
  methods: {
    sectionField(profile, section, field) {
      const sectionId = section;

      if (!profile.sections || !profile.sections[sectionId]) {
        return 'na';
      }

      return profile.sections[sectionId][field];
    },
  },
  setup(props) {
    const sectionFields = ref([
      { key: 'name', label: 'Section Name', sortable: true },
      { key: 'status', sortable: true },
      { key: 'toc', label: 'ToC', sortable: true },
      { key: 'examVoucher', sortable: true },
      { key: 'examTaken', sortable: true },
      { key: 'examPassed', sortable: true },
      { key: 'labs', sortable: true },
      { key: 'retake', label: 'Retake', sortable: true },
      { key: 'makeup', label: 'Makeup', sortable: true },
      // { key: 'actions' },
    ]);
    const sectionsData = ref({});
    const currentPage = ref(1);
    const perPage = usePersistedPerPage('pathwayCohortViewStudentsSectionList', 100);
    const totalSections = ref(0);
    const totalPageCount = ref(1);

    const isSortDirDesc = ref(false);
    const sortBy = ref('startDate');

    const fetchStudentSections = (ctx) => {
      // Sorting by column happens in the browser (sectionSortCompare); the
      // server only orders sections by start date.
      const sb = 'startDate:asc';

      const promise = store.dispatch('studentStoreModule/fetchStudentSections', {
        id: props.studentId,
        queryParams: {
          limit: perPage.value,
          page: currentPage.value,
          populate: 'instructor course',
          sortBy: sb,
          profileIds: [props.profileId],
        },
      });

      return promise
        .then((response) => {
          const { results, totalResults, totalPages } = response.data;

          const resultsMapped = results.map((row, index) => {
            row.index = index + 1;
            return row;
          });

          sectionsData.value = resultsMapped;
          totalSections.value = totalResults;
          totalPageCount.value = totalPages;
          return resultsMapped;
        })
        .catch((error) => {
          console.error(error);
          if (error.response.status === 404) {
            sectionsData.value = undefined;
          }
        });
    };

    const resolveCourseStatusVariant = (status) => {
      if (status === 'created') return 'secondary';
      if (status === 'enrolling') return 'info';
      if (status === 'waiting') return 'warning';
      if (status === 'live') return 'info';
      if (status === 'completed') return 'success';
      return 'info';
    };

    const resolveCourseSectionStartDate = (courseSection) => {
      return momentTzFormatted(courseSection.startDate, 'MMMM Do YYYY');
    };

    const {
      perPageOptions,
      resolveUserRoleVariant,
      resolveCourseSectionName,
      resolveCourseSectionStatusVariant,
      resolveCourseSectionStatusIcon,
      resolveCourseSectionVisibilityVariant,
      resolveCourseSectionVisibilityIcon,
      resolveCourseSectionDateMessage,
      resolveCourseSectionDateMessageIcon,
      resolveCourseSectionDateMessageVariant,
    } = useCourseSections();

    const {
      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,
    } = useProfiles();

    return {
      fetchStudentSections,
      sectionFields,
      sectionsData,
      avatarText,
      title,
      moment,
      resolveCourseStatusVariant,

      resolveUserRoleVariant,
      resolveCourseSectionName,
      resolveCourseSectionStatusVariant,
      resolveCourseSectionStatusIcon,
      resolveCourseSectionVisibilityIcon,
      resolveCourseSectionVisibilityVariant,
      resolveCourseSectionDateMessage,
      resolveCourseSectionDateMessageIcon,
      resolveCourseSectionDateMessageVariant,
      resolveCourseSectionStartDate,

      perPageOptions,

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      sortBy,
      isSortDirDesc,
      currentPage,
      perPage,
      totalSections,
      totalPageCount,
    };
  },
};
</script>
